import { useCallback } from "react";
import { PlatformListToolbar } from "@/components/platform/PlatformListToolbar";
import { usePlatformList } from "@/components/platform/usePlatformList";
import { DataTableDateTime } from "@/components/data-table";
import { AdminAlert, AdminEmptyState, AdminPageHeader, AdminPill } from "@/components/shell/AdminPage";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchPlatformAccounts, type PlatformAccountRow } from "@/lib/platform-api";

const LOCKED_FILTER_OPTIONS = [
  { value: "", label: "全部" },
  { value: "true", label: "已锁定" },
  { value: "false", label: "未锁定" },
] as const;

export default function PlatformAccountsPage() {
  const fetcher = useCallback(async ({ q, filter, page }: { q: string; filter: string; page: number }) => {
    const result = await fetchPlatformAccounts({
      q: q || undefined,
      locked: (filter as "true" | "false" | "") || "",
      page,
    });
    return { rows: result.accounts, meta: result.meta };
  }, []);

  const { inputQ, setInputQ, filter, setFilter, page, setPage, rows, meta, loading, error, submitSearch } =
    usePlatformList<PlatformAccountRow>({ fetcher });

  return (
    <div className="w-full space-y-6">
      <AdminPageHeader title="平台账户" description="查看全平台商家账户（Account）、认证与锁定状态。" />
      <PlatformListToolbar
        searchPlaceholder="搜索账户名称…"
        search={inputQ}
        onSearchChange={setInputQ}
        onSearchSubmit={submitSearch}
        filterValue={filter}
        onFilterChange={setFilter}
        filterOptions={[...LOCKED_FILTER_OPTIONS]}
        meta={meta}
        page={page}
        onPageChange={setPage}
        loading={loading}
      />
      {error ? <AdminAlert>{error}</AdminAlert> : null}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">ID</TableHead>
              <TableHead>账户名称</TableHead>
              <TableHead>所有者</TableHead>
              <TableHead>锁定</TableHead>
              <TableHead className="text-right">成员</TableHead>
              <TableHead className="text-right">品牌</TableHead>
              <TableHead>企业认证</TableHead>
              <TableHead>创建时间</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  加载中…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <AdminEmptyState>
                    <p className="font-medium text-foreground">暂无匹配账户</p>
                    <p className="mt-2">试试调整搜索词或锁定筛选条件。</p>
                  </AdminEmptyState>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((account) => (
                <TableRow key={account.id}>
                  <TableCell className="tabular-nums text-muted-foreground">{account.id}</TableCell>
                  <TableCell className="font-medium">{account.name}</TableCell>
                  <TableCell>{account.owner_label}</TableCell>
                  <TableCell>
                    <AdminPill tone={account.locked ? "warning" : "success"}>
                      {account.locked ? "已锁定" : "未锁定"}
                    </AdminPill>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{account.memberships_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{account.brands_count}</TableCell>
                  <TableCell>
                    {account.enterprise_verified_at ? (
                      <AdminPill tone="success">已认证</AdminPill>
                    ) : (
                      <AdminPill tone="muted">未认证</AdminPill>
                    )}
                  </TableCell>
                  <TableCell>
                    <DataTableDateTime value={account.created_at} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
